import {
  cachedQuery,
  clearQueryCache,
  invalidateQueryCache,
  isCachedQuery,
} from './queryCache';
/**
 * Centralized fetch wrapper that detects 401 responses and dispatches
 * a session-expired event so AuthContext can log the user out.
 *
 * All API services should use fetchWithAuth() instead of native fetch().
 */

let cacheIdentity: string | undefined;

export const SESSION_EXPIRED_EVENT = 'auth:session-expired';

export async function fetchWithAuth(
  input: string | URL,
  init?: RequestInit
): Promise<Response> {
  const url = input instanceof URL ? input.toString() : input;
  let identity = new Headers(init?.headers).get('Authorization') || '';
  try {
    identity += `:${localStorage.getItem('auth_token') || ''}`;
  } catch {
    /* Session cookies still work without localStorage. */
  }
  if (cacheIdentity !== identity) {
    clearQueryCache();
    cacheIdentity = identity;
  }
  const parsed = new URL(url, window.location.origin);
  parsed.searchParams.sort();
  const method = (init?.method || 'GET').toUpperCase();
  const load = async () => {
    const response = await fetch(url, { ...init, credentials: 'include' });

    if (response.status === 401) {
      clearQueryCache();
      try {
        if (localStorage.getItem('auth_token')) {
          window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
        }
      } catch {
        // Ignore if localStorage is unavailable
      }
    }

    if (
      response.ok &&
      !['GET', 'HEAD', 'OPTIONS'].includes(method) &&
      !parsed.pathname.endsWith('/transactions/category-suggestion/')
    ) {
      if (/\/auth\/(login|demo-login|logout|register)\//.test(parsed.pathname))
        clearQueryCache();
      else if (
        /\/(accounts|transactions|plaid|budgets|household)\//.test(
          parsed.pathname
        ) ||
        /\/auth\/categor/.test(parsed.pathname)
      )
        invalidateQueryCache();
    }
    return response;
  };
  // Abortable and explicitly uncached requests retain their caller's semantics.
  if (
    method === 'GET' &&
    !init?.signal &&
    init?.cache !== 'no-store' &&
    isCachedQuery(parsed)
  ) {
    return cachedQuery(parsed.toString(), load);
  }
  return load();
}
